import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Dumbbell, Clock, Flame, Play,
  Timer, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { fitnessApi } from '../services/api';
import { toast } from '../hooks/use-toast';

const ProgramDetailPage = () => {
  const { programId } = useParams();
  const { language } = useLanguage();
  const [program, setProgram] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [programId]);

  const fetchData = async () => {
    try {
      const [progData, workoutData] = await Promise.all([
        fitnessApi.getProgram(programId),
        fitnessApi.getProgramWorkouts(programId)
      ]);
      setProgram(progData);
      setWorkouts(workoutData);
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: language === 'en' ? 'Failed to load program' : 'Error al cargar el programa',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex items-center justify-center">
        <p className="text-gray-400">{language === 'en' ? 'Program not found' : 'Programa no encontrado'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1f1f]">
      {/* Hero */}
      <div className="relative h-64 md:h-80 bg-gradient-to-br from-emerald-500/20 to-teal-500/20">
        {program.image_url && (
          <img 
            src={program.image_url} 
            alt={program.name_en}
            className="w-full h-full object-cover opacity-50"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f1f] to-transparent" />
        
        {/* Back button */}
        <div className="absolute top-4 left-4">
          <Link to="/programs">
            <Button variant="ghost" size="icon" className="bg-black/30 backdrop-blur-sm text-white hover:bg-black/50">
              <ArrowLeft className="w-6 h-6" />
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-20 relative z-10 pb-8">
        {/* Program Info */}
        <div className="bg-gray-900/80 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-gray-800 mb-6">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="bg-emerald-500/20 text-emerald-400 text-xs font-medium px-3 py-1 rounded-full">
              {program.category}
            </span>
            <span className="bg-gray-700/50 text-gray-300 text-xs font-medium px-3 py-1 rounded-full">
              {program.difficulty}
            </span>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {language === 'en' ? program.name_en : program.name_es}
          </h1>

          <p className="text-gray-400 mb-6">
            {language === 'en' ? program.description_en : program.description_es}
          </p>

          <div className="flex flex-wrap gap-6">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-emerald-400" />
              <span className="text-white">{program.duration_weeks} {language === 'en' ? 'weeks' : 'semanas'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Dumbbell className="w-5 h-5 text-emerald-400" />
              <span className="text-white">{workouts.length} {language === 'en' ? 'workouts' : 'entrenamientos'}</span>
            </div>
          </div>
        </div>

        {/* Workouts List */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-white mb-4">
            {language === 'en' ? 'Workouts' : 'Entrenamientos'}
          </h2>

          {workouts.map((workout, index) => (
            <Link
              key={workout.id}
              to={`/workout/${workout.id}`}
              className="block bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-800 hover:border-emerald-500/30 transition-all group"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">
                      {index + 1}
                    </div>
                    <h3 className="text-lg font-semibold text-white group-hover:text-emerald-400 transition-colors">
                      {language === 'en' ? workout.name_en : workout.name_es}
                    </h3>
                  </div>
                  
                  <p className="text-gray-400 text-sm mb-4 ml-11">
                    {language === 'en' ? workout.description_en : workout.description_es}
                  </p>

                  <div className="flex flex-wrap gap-4 ml-11">
                    <div className="flex items-center space-x-1 text-sm text-gray-500">
                      <Timer className="w-4 h-4" />
                      <span>{workout.duration_minutes} min</span>
                    </div>
                    <div className="flex items-center space-x-1 text-sm text-gray-500">
                      <Flame className="w-4 h-4" />
                      <span>{workout.calories_estimate} kcal</span>
                    </div>
                    <div className="flex items-center space-x-1 text-sm text-gray-500">
                      <Zap className="w-4 h-4" />
                      <span>{workout.difficulty}</span>
                    </div>
                    <div className="flex items-center space-x-1 text-sm text-gray-500">
                      <Dumbbell className="w-4 h-4" />
                      <span>{workout.exercises?.length || 0} {language === 'en' ? 'exercises' : 'ejercicios'}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    <Play className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Start' : 'Iniciar'}
                  </Button>
                </div>
              </div>
            </Link>
          ))}

          {workouts.length === 0 && (
            <div className="text-center py-12 bg-gray-900/50 rounded-2xl border border-gray-800">
              <Dumbbell className="w-12 h-12 text-gray-700 mx-auto mb-4" />
              <p className="text-gray-500">
                {language === 'en' ? 'No workouts available yet' : 'Aún no hay entrenamientos disponibles'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgramDetailPage;
